import { useState } from 'react'

import { Button, SelectField, StatusMessage, TextField } from '@krp/ui-kit'

import type { AqsiCreateDefaults, CreateDefaultValues, CreateField } from '../model/types'

export interface CreateDefaultsScreenProps {
  /** Умолчания и шаблон; `null` — ещё не прочитаны. */
  data: AqsiCreateDefaults | null
  busy: boolean
  error: { message: string; meta: string } | null
  saved: boolean
  onSave: (values: CreateDefaultValues) => void
  onReload: () => void
}

const NOT_SET = ''

function initial(data: AqsiCreateDefaults | null): Record<string, string> {
  return Object.fromEntries((data?.fields ?? []).map((field) => [field.key, field.defaultValue ?? NOT_SET]))
}

function FieldInput({ field, value, disabled, onChange }: { field: CreateField; value: string; disabled: boolean; onChange: (value: string) => void }) {
  if (field.type === 'choice')
    return <SelectField
      label={field.label}
      value={value}
      options={[{ value: NOT_SET, label: '— не задано —' }, ...field.options]}
      disabled={disabled}
      onChange={onChange}
    />
  return <TextField label={field.label} value={value} onChange={onChange} />
}

/**
 * «Создание по умолчанию» источника AQSI (RP-SYN12, SYNC-V2-PLAN 3.7): с какой категорией, ставкой, единицей и предметом расчёта заводится карточка в
 * кабинете, когда человек создаёт её в разделе «Синхронизация», а у источника этих полей нет. В окне применения значения предзаполнены отсюда и правятся
 * для всех строк или для одной. Состав полей — от службы AQSI (шаблон создания), окно его не придумывает.
 */
export function CreateDefaultsScreen(props: CreateDefaultsScreenProps) {
  const [draft, setDraft] = useState(() => initial(props.data))
  const data = props.data
  const changed = data !== null && data.fields.some((field) => (draft[field.key] ?? NOT_SET) !== (field.defaultValue ?? NOT_SET))
  const missing = data?.fields.filter((field) => field.required && (draft[field.key] ?? NOT_SET).trim() === NOT_SET) ?? []
  return (
    <section className="aqsi-card" aria-label="Создание по умолчанию">
      <h2>Создание по умолчанию</h2>
      <p>
        Эти значения подставляются, когда карточка создаётся в кабинете AQSI из раздела «Синхронизация», а у источника такого поля нет. Значение источника
        важнее: созданная карточка совпадает с ним. При применении любое значение можно поменять для всех выбранных строк или для одной.
      </p>
      {data === null && !props.error && <StatusMessage kind="loading" title="Читаем умолчания и справочники кабинета…" />}
      {props.error && <StatusMessage kind="error" title="Создание по умолчанию" text={props.error.message} meta={props.error.meta} />}
      {data !== null && data.problems.length > 0 && <ul aria-label="Что требует внимания">
        {data.problems.map((problem) => <li key={problem}>⚠ {problem}</li>)}
      </ul>}
      {data?.fields.map((field) => (
        <FieldInput
          key={field.key}
          field={field}
          value={draft[field.key] ?? NOT_SET}
          disabled={props.busy}
          onChange={(value) => setDraft((current) => ({ ...current, [field.key]: value }))}
        />
      ))}
      {missing.length > 0 && <p>
        Не задано: {missing.map((field) => `«${field.label}»`).join(', ')}. Без них создание откажет со словами «не задано поле», если и источник его не сообщит
        и при применении его не выберут.
      </p>}
      {data !== null && data.version > 0 && <p>
        Сохранено{data.updatedBy === null ? '' : ` — ${data.updatedBy}`}{data.updatedAt === null ? '' : `, ${new Date(data.updatedAt).toLocaleString('ru-RU')}`}.
      </p>}
      <div className="aqsi-card__actions">
        <Button
          disabled={props.busy || data === null || !changed}
          onClick={() => {
            if (data === null) return
            props.onSave(Object.fromEntries(data.fields.map((field) => {
              const value = (draft[field.key] ?? NOT_SET).trim()
              return [field.key, value === NOT_SET ? null : value]
            })))
          }}
        >
          {props.busy ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        <Button variant="ghost" disabled={props.busy} onClick={props.onReload}>Обновить</Button>
      </div>
      {props.saved && !changed && <p role="status">Умолчания сохранены для этого источника.</p>}
    </section>
  )
}
